import type { StatusTone } from "../data-display/StatusBadge";

/**
 * Workflow automation model. A workflow is TRIGGER -> CONDITIONS -> ordered
 * ACTIONS with per-action delays. Triggers are real database events, so
 * automation fires regardless of which surface caused the change; execution
 * is asynchronous (pg_cron engine) and every attempt is recorded on the run
 * ledger, including consent suppressions.
 */

export interface Workflow {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  trigger_type: string;
  trigger_config: Record<string, string>;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WorkflowAction {
  id: string;
  workflow_id: string;
  position: number;
  action_type: string;
  config: Record<string, string>;
  conditions?: { if_no_reply?: boolean; if_no_reply_hours?: number } | null;
  delay_minutes: number;
}

export interface WorkflowRun {
  id: string;
  workflow_id: string;
  context: Record<string, unknown>;
  status: string;
  current_action: number;
  next_run_at: string | null;
  completed_at: string | null;
  error: string | null;
  created_at: string;
}

export interface WorkflowRunStep {
  id: string;
  run_id: string;
  position: number;
  action_type: string;
  status: string;
  detail: string | null;
  created_at: string;
}

export const TRIGGERS = [
  {
    value: "contact.created",
    label: "A contact is created",
    conditionKey: null,
  },
  {
    value: "deal.stage_changed",
    label: "A deal moves stage",
    conditionKey: "to_stage",
    conditionLabel: "Only when the new stage is",
  },
  { value: "job.completed", label: "A job is completed", conditionKey: null },
  {
    value: "estimate.accepted",
    label: "An estimate is accepted",
    conditionKey: null,
  },
  {
    value: "estimate.declined",
    label: "An estimate is declined",
    conditionKey: null,
  },
  { value: "invoice.paid", label: "An invoice is paid", conditionKey: null },
  {
    value: "invoice.overdue",
    label: "An invoice goes overdue",
    conditionKey: null,
  },
  {
    value: "form.submitted",
    label: "A form is submitted",
    conditionKey: "form_id",
    conditionLabel: "Only for form",
  },
  // These three have existed in the engine since the communications build, // webhooks and sweeps emit them, but were never offered here, so the
  // automations they enable (missed-call text-back, reply triage,
  // appointment reminders) could not actually be built from the UI.
  {
    value: "message.received",
    label: "A customer messages us",
    conditionKey: "channel",
    conditionLabel: "Only on channel (sms/email/call)",
  },
  {
    value: "call.missed",
    label: "A call is missed",
    conditionKey: null,
  },
  {
    value: "appointment.reminder",
    label: "An appointment is coming up",
    conditionKey: null,
  },
  {
    value: "estimate.unanswered",
    label: "An estimate got no answer",
    conditionKey: null,
  },
  {
    value: "message.clicked",
    label: "A customer clicks an email link",
    conditionKey: null,
  },
] as const;

export const ACTIONS = [
  { value: "create_task", label: "Create a follow-up task" },
  { value: "send_message", label: "Send an email or text" },
  { value: "enroll_sequence", label: "Enroll in a sequence" },
  { value: "set_lifecycle_stage", label: "Set the lifecycle stage" },
  { value: "add_tag", label: "Add a tag" },
  { value: "set_deal_stage", label: "Move the deal to a stage" },
  { value: "create_job", label: "Create a job" },
  { value: "ai_draft_followup", label: "AI drafts a follow-up (for review)" },
] as const;

export const triggerLabel = (value?: string) =>
  TRIGGERS.find((t) => t.value === value)?.label ?? value ?? "";

export const actionLabel = (value?: string) =>
  ACTIONS.find((a) => a.value === value)?.label ?? value ?? "";

export const triggerDefinition = (value?: string) =>
  TRIGGERS.find((t) => t.value === value);

export const RUN_STATUS_TONES: Record<string, StatusTone> = {
  pending: "neutral",
  running: "info",
  completed: "success",
  failed: "danger",
  cancelled: "warning",
};

export const RUN_STEP_TONES: Record<string, StatusTone> = {
  succeeded: "success",
  skipped: "neutral",
  // Consent refused the action: recorded, never silently "successful".
  suppressed: "warning",
  failed: "danger",
};

/** "immediately" / "after 30 minutes" / "after 2 days" */
export const describeDelay = (minutes: number, isFirst: boolean): string => {
  if (!minutes) return isFirst ? "immediately" : "right after";
  if (minutes < 60) return `after ${minutes} minute${minutes === 1 ? "" : "s"}`;
  if (minutes < 1440) {
    const hours = Math.round(minutes / 60);
    return `after ${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const days = Math.round(minutes / 1440);
  return `after ${days} day${days === 1 ? "" : "s"}`;
};

export const DELAY_CHOICES = [
  { value: 0, label: "Immediately" },
  { value: 15, label: "After 15 minutes" },
  { value: 60, label: "After 1 hour" },
  { value: 1440, label: "After 1 day" },
  { value: 4320, label: "After 3 days" },
  { value: 10080, label: "After 7 days" },
] as const;
